import {
  isAfter,
  closestTo,
  parseISO,
  format,
  parse,
  formatISO,
} from "date-fns";

import config from "../config/config";

export const cacheBustString = () => {
  // Cache-bust string based on current date and hour
  const date = new Date();
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const hour = date.getHours().toString().padStart(2, "0");

  return `${day}${month}${hour}`;
};

export const getBandsByTicketType = (ticketType, bands) => {
  if (!bands) return;

  const band_map = Object.entries(bands);
  return band_map
    .filter(([key, band]) => band && band["colour"])
    .map(([key, band]) => {
      return {
        band: key,
        amount: band[ticketType],
        color: band["colour"],
        id: band["id"],
      };
    });
};

export const parseSeatStatus = (seatStatusData) => {
  if (!seatStatusData) return;
  // console.debug("Parsing seat status data:", seatStatusData);
  const seatStatusMap = {};
  const seatInfoMap = {};

  seatStatusData.split(/\r\n|\n|\r/).forEach((entry) => {
    // Match status lines like A, B, U, W, X

    // we can ignore SC seat statuses as those are Masked and Scanned
    if (!entry || entry.startsWith("SC:")) {
      return;
    }

    const status_matches = entry.match(/^((?:SO|[ABUWXLM])):(.+?):([\d,]+)$/);
    if (status_matches) {
      const [_, status, priceBandOrObj, seatIds] = status_matches;

      let lockType = null;
      let priceBandId = priceBandOrObj;

      // Match any string inside the {...} structure
      const nestedMatch = priceBandOrObj.match(/\{[^:]+:"([^"]+)"\}/);
      if (nestedMatch) {
        lockType = nestedMatch[1];

        // Get outer priceBandId before the {..}
        const mainIdMatch = priceBandOrObj.match(/^([^:]+):\{.+\}$/);
        if (mainIdMatch) {
          priceBandId = mainIdMatch[1];
        }
      }

      seatIds.split(",").forEach((seatId) => {
        seatStatusMap[seatId] = {
          priceBandId,
          status,
          ...(lockType ? { lockType } : {}),
        };
      });
    }

    // Match info lines like I:INFO:1234
    const info_matches = entry.match(/^([I]):(.+?):([\d,]+)$/);
    if (info_matches) {
      const [_, status, priceBandId, seatIds] = info_matches;

      seatIds.split(",").forEach((seatId) => {
        seatInfoMap[seatId] = {
          priceBandId,
          status,
        };
      });
    }
  });

  // Remove any seatIds that are in a M (Masked) status,
  // and also remove any Ls that have the same seatId as an M
  const maskedSeatIds = Object.keys(seatStatusMap).filter(
    (seatId) => seatStatusMap[seatId].status === "M"
  );

  // console.debug("Masked seat IDs:", maskedSeatIds);

  // Now remove any Ls that have the same seatId as an M (already deleted above)
  Object.keys(seatStatusMap).forEach((seatId) => {
    if (
      seatStatusMap[seatId].status === "L" &&
      maskedSeatIds.includes(seatId)
    ) {
      delete seatStatusMap[seatId];
    }
  });

  //remove the M seats from the seatInfoMap as well
  maskedSeatIds.forEach((seatId) => {
    delete seatInfoMap[seatId];
  });

  return { status: seatStatusMap, info: seatInfoMap };
};

export const parseSeatDetailObject = (seatStatusData) => {
  return seatStatusData;
};

export const getTicketsByBand = (band, pricelist) => {
  if (!pricelist) return;

  return pricelist?.filter((price) => {
    return price.priceBand.name == band;
  });
};

export const formatDateTime = (
  dateTimeStr,
  separateDateFromTime = false,
  includeYear = false
) => {
  if (!dateTimeStr) return "";
  try {
    const date = parseISO(dateTimeStr);
    if (separateDateFromTime) {
      return {
        date: format(date, includeYear ? "EEE d MMM yyyy" : "EEE d MMM"),
        time: format(date, "h:mma"),
      };
    }
    if (includeYear) {
      return format(date, "EEE d MMM yyyy, h:mma");
    }
    return format(date, "EEE d MMM yyyy, h:mma");
  } catch (error) {
    return "Invalid Date";
  }
};

export const getTicketsByInstance = (
  tickets,
  instance_id = config.INSTANCE_ID
) => {
  return tickets.filter((ticket) => ticket.instance.id == instance_id);
};

export const getSupplementaryProtectionId = (tickets) => {
  const supplementaries = tickets.filter((ticket) => (config.SUPPLEMENTARY_INSTANCES ?? []).includes(ticket.instance.id))
  console.log("tickets", supplementaries)
  return supplementaries.find((supplementary) => supplementary.event.name.toLowerCase().includes("refund"))?.instance.id
}

export const groupTicketsByTicketType = (tickets) => {
  const grouped_tickets = tickets.reduce((acc, item) => {
    (acc[item.ticketType.name] = acc[item.ticketType.name] || []).push(item);
    return acc;
  }, {});

  const mapped_tickets = Object.entries(grouped_tickets).map(
    ([name, tickets]) => {
      return {
        name: name,
        count: tickets.length,
        tickets: tickets,
        seats: tickets
          .map((ticket) => ticket.seat?.name)
          .filter((notUndefined) => notUndefined !== undefined),
        seats_group: tickets
          ?.map((ticket) => ticket.seat?.name)
          .filter((notUndefined) => notUndefined !== undefined),
        total: (tickets[0].total * tickets.length).toFixed(2),
      };
    }
  );
  return mapped_tickets;
};

export const groupPriceListByAmount = (priceListData, enabledFilters = []) => {
  if (!priceListData) return [];

  const grouped = priceListData.reduce((acc, item) => {
    const key = item.amount;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const sorted = Object.entries(grouped)
    .filter(([amount]) => enabledFilters.includes(amount.toString()))
    .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
    .map(([amount, items], index) => ({
      amount: parseFloat(amount),
      items,
      color:
        items?.[0]?.priceBand?.hexCode ??
        (config.COLORS?.length ? config.COLORS[index] : null),
    }));

  return sorted;
};

export const addCommissionToPriceList = (priceListData) => {
  if (!priceListData) return;

  return priceListData.map((price) => {
    let commission = 0;
    if (price.customItems && price.customItems.length > 0) {
      commission =
        price.customItems[0].name == "Commission"
          ? parseFloat(price.customItems[0].amount)
          : 0;
    }

    return {
      ...price,
      amount: parseFloat(price.amount) + commission,
    };
  });
};

export const getBandsByPrice = (bands) => {
  if (!bands) return;

  const grouped_bands = bands.reduce((acc, item) => {
    (acc[item.amount] = acc[item.amount] || []).push(item);
    return acc;
  }, {});

  const mapped_bands = Object.entries(grouped_bands).map(([amount, bands]) => {
    return {
      amount: amount,
      band_ids: bands.map((band) => band.priceBand?.id),
      bands: bands.map((band) => band),
    };
  });

  return mapped_bands;
};

export const groupTicketsByInstance = (tickets) => {
  const grouped_tickets = tickets.reduce((acc, item) => {
    (acc[item.instance?.id] = acc[item.instance?.id] || []).push(item);
    return acc;
  }, {});

  const mapped_tickets = Object.entries(grouped_tickets).map(
    ([id, tickets]) => {
      const ticket = tickets[0];
      const event = ticket.event?.name ? ticket.event : ticket.instance?.event;

      return {
        event_id: event?.id,
        event_name: event?.name,
        event_venue: event
          ? event[`attribute_${config.OPTION_FIELDS.venue_attribute_name}`]
          : "",
        event_image: event?.thumbnailUrl,
        instance_id: id,
        instance_date: formatDateTime(ticket.instance?.start),
        instance_date_utc: ticket.instance?.startUtc,
        tickets: tickets,
        tickets_total: getTicketsTotal(tickets),
        seats: tickets
          ?.map((ticket) => {
            return {
              seat: ticket.seat,
              plan_name: ticket.planName,
              ticket: ticket,
            };
          })
          .filter((notUndefined) => notUndefined !== undefined),
      };
    }
  );

  return mapped_tickets;
};

export const getTicketsTotal = (tickets) => {
  return (
    tickets?.reduce((sum, ticket) => {
      const ticketTotal = Number(ticket.total) || 0;
      return sum + ticketTotal;
    }, 0) || 0
  );
};

export const getGroupedTicketsTotal = (tickets) => {
  // Uses the data from groupTicketsByInstance()
  return tickets?.reduce((sum, ticket) => sum + ticket.tickets_total, 0) || 0;
};

export const getMembershipsTotal = (memberships) => {
  return (
    memberships?.reduce((sum, membership) => sum + membership.total, 0) || 0
  );
};

export const getDonationsTotal = (donations) => {
  return donations?.reduce((sum, donation) => sum + donation.amount, 0) || 0;
};

export const getDeliveryAddress = (data) => {
  if (!data) return;

  const filtered_addresses = data?.customer?.addresses?.filter(
    (address) => address.isDelivery
  );
  if (!filtered_addresses || !filtered_addresses.length) return;

  const address = filtered_addresses[0];
  return {
    Country: address.country.isoCode,
    AdministrativeDivision: address.administrativeDivision.name,
    Name: address.name,
    Line1: address.line1,
    Line2: address.line2,
    Line3: address.line3,
    Line4: address.line4,
    Line5: address.line5,
    Postcode: address.postcode,
    Town: address.town,
  };
};

export const getDeliveryAmount = (data) => {
  if (!data) return;

  const current_delivery_type = data.ticketDelivery
    ? data.ticketDelivery.type
    : "PrintAtHome";

  const delivery_options = data.ticketDeliveryOptions?.filter(
    (option) => current_delivery_type == option.type
  );

  return delivery_options?.length ? delivery_options[0].fee.toFixed(2) : null;
};

export const getBasketTicketById = (basketData, seatId) => {
  const item = basketData?.tickets?.find((ticket) => {
    return ticket.seat.id == seatId;
  });
  if (!item) throw new Error(`No tickets with provided ID are in the basket`);

  return item;
};

export const findClosestDate = (arr, dateProp) => {
  const now = new Date();
  const dates = arr.map((item) => parseISO(item[dateProp]));

  const closestDate = closestTo(now, dates);

  return arr.find(
    (item) => parseISO(item[dateProp]).getTime() === closestDate.getTime()
  );
};

export const getUpcomingTickets = (orders, removeDuplicates = false) => {
  const now = new Date();

  const upcoming = orders.flatMap((order) =>
    order.tickets
      .filter(
        (ticket) =>
          ticket.instance && isAfter(parseISO(ticket.instance.start), now)
      )
      .map((ticket) => ({
        ...ticket,
        orderId: order.id,
      }))
  );

  if (!removeDuplicates) return upcoming;

  // Remove duplicates by instance ID
  const deduped_upcoming = upcoming.filter(
    (ticket, index, self) =>
      index === self.findIndex((t) => t.instance.id === ticket.instance.id)
  );
  return deduped_upcoming;
};

export const getUpcomingOrders = (orders) => {
  const now = new Date();
  return orders.filter((order) =>
    order.tickets
      .filter(
        (ticket) =>
          ticket.instance && isAfter(parseISO(ticket.instance.start), now)
      )
      .map((ticket) => ({
        orderId: order.id,
        instanceId: ticket.instance.id,
      }))
  );
};

export const getPastOrders = (orders) => {
  const now = new Date();
  return orders.flatMap((order) =>
    order.tickets.filter(
      (ticket) =>
        ticket.instance && isAfter(parseISO(ticket.instance.start), now)
    )
  );
};

export const getTotalDonations = (orders) => {
  return orders.reduce((sum, order) => {
    const donationSum = order.donations.reduce(
      (acc, donation) => acc + donation.amount,
      0
    );
    return sum + donationSum;
  }, 0);
};

export const getAudienceTagIds = () => {
  return config.TAGS?.map((tag) => tag.id);
};

export const getCustomerTagIds = (tags) => {
  // Some tags in customer tags won't be part of the audience tags list
  // Use this variable to filter only the tags we want to add and remove
  const audience_tag_ids = getAudienceTagIds();
  return tags
    ?.map((tag) => {
      if (audience_tag_ids?.includes(tag.id)) return tag.id;
    })
    .filter((id) => id !== undefined);
};

export const ticketsHaveDonationUpsell = (tickets) => {
  return tickets?.some(
    (ticket) => ticket.event?.attribute_WebsiteDonationUpsell === true
  );
};

export const datePickerToISOString = (dateStr) => {
  // Can be used to convert date picker format to format expected by Spektrix
  const parsed = parse(dateStr, "yyyy-MM-dd", new Date());
  const iso = formatISO(parsed, { representation: "complete" });
  return iso.split(".")[0];
};

export const findDonationByRecognitionName = (donations, name) => {
  return donations?.find((donation) => donation.recognitionName == name);
};

export const getUniqueDates = (instances) => {
  return [...new Set(instances.map((instance) => instance.start.slice(0, 10)))];
};

export const getExcludedDonations = (basketData) => {
  // if donations are related to memberships they shouldn't be displayed as separate entities
  return basketData?.membershipSubscriptions
    ?.map((membership) => {
      if (membership.relatedDonationId) return membership.relatedDonationId;
    })
    .filter((notUndefined) => notUndefined !== undefined);
};

export const isValidSeatSelection = (runs) => {
  const isAvailable = (status) => status === "A";

  for (const run of runs) {
    const leftStatuses = run.leftStatuses;
    const rightStatuses = run.rightStatuses;

    let leftCount = 0;
    let rightCount = 0;

    // Count consecutive available seats to the left (starting from edge closest to run)
    for (let i = 0; i < leftStatuses.length; i++) {
      if (isAvailable(leftStatuses[i].status)) {
        leftCount++;
      } else {
        break;
      }
    }

    // Count consecutive available seats to the right (starting from edge closest to run)
    for (let i = 0; i < rightStatuses.length; i++) {
      if (isAvailable(rightStatuses[i].status)) {
        rightCount++;
      } else {
        break;
      }
    }

    // Apply rules table
    if (leftCount === 0) continue; // valid
    if (leftCount === 1 && rightCount === 0) continue; // valid
    if (leftCount >= 2 && (rightCount === 0 || rightCount >= 2)) continue; // valid

    // Any other combination is invalid
    return false;
  }

  return true; // all runs valid
};

export const getSeatRunsById = (selectedSeatIds, allSeats) => {
  if (!selectedSeatIds || selectedSeatIds.length === 0) return [];

  // Build a lookup map for faster seatId -> seat
  const seatMap = new Map(allSeats.map((s) => [String(s.id), s]));

  // Convert selectedSeatIds to seat objects, skipping invalid IDs
  const selectedSeats = selectedSeatIds
    .map((id) => seatMap.get(String(id)))
    .filter(Boolean);

  if (selectedSeats.length === 0) return [];

  // Sort by row and number
  const seatsSorted = [...selectedSeats].sort((a, b) => {
    if (a.row === b.row) return a.number - b.number;
    return a.row.localeCompare(b.row);
  });

  const runs = [];
  let runStart = seatsSorted[0];
  let runEnd = seatsSorted[0];

  for (let i = 1; i < seatsSorted.length; i++) {
    const seat = seatsSorted[i];

    // Check if consecutive
    const isConsecutive =
      (runEnd.nextSeatId && runEnd.nextSeatId === seat.id) ||
      (runEnd.row === seat.row && seat.number === runEnd.number + 1);

    if (isConsecutive) {
      runEnd = seat; // extend the run
    } else {
      // Finish the previous run
      runs.push({ left: runStart.id, right: runEnd.id });
      runStart = seat;
      runEnd = seat;
    }
  }

  // Push the last run
  runs.push({ left: runStart.id, right: runEnd.id });

  return runs;
};

export const getStatusesAroundRuns = (runs, allSeats, statuses) => {
  const seatMap = new Map(allSeats.map((s) => [String(s.id), s]));

  const results = [];

  for (const run of runs) {
    const leftEdge = seatMap.get(String(run.left));
    const rightEdge = seatMap.get(String(run.right));

    if (!leftEdge || !rightEdge) continue;

    // Helper to collect up to n seats in a direction, starting **outside the run**
    const collectSeatsOutside = (startSeat, direction = "left", n = 3) => {
      const collected = [];
      let seat = null;

      if (
        direction === "left" &&
        startSeat.previousSeatId &&
        !startSeat.previousIsAcrossAisle
      ) {
        seat = seatMap.get(String(startSeat.previousSeatId));
      } else if (
        direction === "right" &&
        startSeat.nextSeatId &&
        !startSeat.nextIsAcrossAisle
      ) {
        seat = seatMap.get(String(startSeat.nextSeatId));
      }

      while (seat && collected.length < n) {
        const status = statuses[seat.id]?.status ?? null;
        collected.push({ id: seat.id, status });

        // Move further in the same direction
        if (direction === "left") {
          if (!seat.previousSeatId || seat.previousIsAcrossAisle) break;
          seat = seatMap.get(String(seat.previousSeatId));
        } else {
          if (!seat.nextSeatId || seat.nextIsAcrossAisle) break;
          seat = seatMap.get(String(seat.nextSeatId));
        }
      }

      return collected;
    };

    const leftStatuses = collectSeatsOutside(leftEdge, "left", 3);
    const rightStatuses = collectSeatsOutside(rightEdge, "right", 3);

    results.push({
      run,
      leftStatuses,
      rightStatuses,
    });
  }

  return results;
};
